/**
 * CreditNoteDialog — modal for issuing a GST-compliant credit note
 * against an invoice.
 *
 * Contract (matches POST /api/billing/invoices/{invoice_id}/credit-notes):
 *   - reason_code + reason_text (min 5 chars; "other" requires 10+)
 *   - settlement_mode = "adjustment" | "refund"
 *   - lines: [{line_id, quantity_credited}] — at least 1 with qty > 0
 *   - refund_method + refund_reference (only when mode="refund")
 *
 * UX rules:
 *   - Default reason code = "data_error" (most common driver for
 *     amending an invoice), auto-fills reason_text placeholder.
 *   - Table shows every line on the source invoice with original qty
 *     and a per-line credit-qty input (0 = don't credit).
 *   - Live-computes: subtotal, tax reversal (CGST/SGST/IGST or
 *     summed as tax_total when we don't know split), rounded_total.
 *   - Settlement mode auto-switches based on invoice state:
 *       * paid_total > 0 → default "refund"; disables "adjustment"
 *         if due_total <= 0.
 *       * due_total > 0  → default "adjustment"; refund still
 *         allowed for any paid-portion.
 *   - Submit disabled until at least one line qty > 0 AND reason_text
 *     valid AND (mode=="refund" ⇒ refund_method set).
 */
import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import { API, fmtINR, splitGst } from './billingUtils';

const REASON_CODES = [
  { value: 'data_error',          label: 'Data entry error (wrong patient / SKU / test)' },
  { value: 'price_adjustment',    label: 'Price adjustment (post-sale correction)' },
  { value: 'post_sale_discount',  label: 'Post-sale discount / goodwill' },
  { value: 'return',              label: 'Return of goods' },
  { value: 'service_deficiency',  label: 'Service was not delivered / deficient' },
  { value: 'duplicate_billing',   label: 'Duplicate billing — this bill is redundant' },
  { value: 'other',               label: 'Other (please specify)' },
];

const REFUND_METHODS = [
  { value: 'cash',          label: 'Cash' },
  { value: 'upi',           label: 'UPI' },
  { value: 'card',          label: 'Card' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'insurance',     label: 'Insurance' },
];

export default function CreditNoteDialog({ invoice, onClose, onIssued }) {
  const [reasonCode, setReasonCode] = useState('data_error');
  const [reasonText, setReasonText] = useState('');
  const [mode, setMode] = useState(() => {
    // Sensible default: adjustment when there's outstanding due; refund
    // otherwise (matches how operators think about it).
    if ((invoice?.paid_total || 0) > 0 && (invoice?.due_total || 0) <= 0.01) return 'refund';
    if ((invoice?.due_total || 0) > 0.01) return 'adjustment';
    return 'refund';
  });
  const [refundMethod, setRefundMethod] = useState('upi');
  const [refundRef, setRefundRef] = useState('');
  const [lineQtys, setLineQtys] = useState(() => {
    const seed = {};
    (invoice?.lines || []).forEach((ln) => { seed[ln.line_id] = 0; });
    return seed;
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Prev-credited quantities are inferred from `credit_notes_credited_qty`
  // if present on the invoice (denormalized by the backend). Fallback:
  // treat every line as fully creditable — the backend still enforces
  // over-credit at issue time so an optimistic UI is safe.
  const prevCredited = invoice?.credit_notes_credited_qty || {};

  const compact = (n) => Math.round(n * 100) / 100;

  const rows = useMemo(() => {
    return (invoice?.lines || []).map((ln) => {
      const origQty = Number(ln.quantity || 0);
      const already = Number(prevCredited[ln.line_id] || 0);
      const remaining = Math.max(0, compact(origQty - already));
      const qty = Number(lineQtys[ln.line_id] || 0);
      const share = origQty > 0 ? qty / origQty : 0;
      const taxable = compact(Number(ln.taxable_value || 0) * share);
      const { cgst, sgst } = splitGst({ tax_total: compact((Number(ln.cgst_amount || 0) + Number(ln.sgst_amount || 0)) * share) });
      const igst = compact(Number(ln.igst_amount || 0) * share);
      const total = compact(taxable + cgst + sgst + igst);
      return { ...ln, origQty, already, remaining, qty, taxable, cgst, sgst, igst, total };
    });
  }, [invoice, lineQtys, prevCredited]);

  const totals = useMemo(() => {
    const t = { subtotal: 0, cgst: 0, sgst: 0, igst: 0, total: 0 };
    rows.forEach((r) => {
      t.subtotal += r.taxable;
      t.cgst += r.cgst;
      t.sgst += r.sgst;
      t.igst += r.igst;
      t.total += r.total;
    });
    // Round to nearest rupee for the "amount credited" figure.
    Object.keys(t).forEach((k) => { t[k] = compact(t[k]); });
    t.rounded = Math.round(t.total);
    return t;
  }, [rows]);

  const setQty = (lineId, value) => {
    const v = Math.max(0, Number(value || 0));
    setLineQtys((prev) => ({ ...prev, [lineId]: v }));
  };

  const canRefund  = (invoice?.paid_total || 0) > 0.01;
  const canAdjust  = (invoice?.due_total  || 0) > 0.01;
  // Force-switch away from a mode that isn't available on this invoice.
  useEffect(() => {
    if (mode === 'refund' && !canRefund && canAdjust) setMode('adjustment');
    if (mode === 'adjustment' && !canAdjust && canRefund) setMode('refund');
  }, [mode, canRefund, canAdjust]);

  const reasonMinLen = reasonCode === 'other' ? 10 : 5;
  const reasonOK = (reasonText.trim().length >= reasonMinLen);
  const hasQty = rows.some((r) => r.qty > 0);
  const refundOK = (mode !== 'refund') || !!refundMethod;
  // Settlement-capacity check. If the computed credit exceeds the
  // invoice's available settlement capacity for the selected mode
  // (adjustment capped at due_total, refund capped at paid_total), we
  // block submit AND surface an explicit inline reason so the user
  // doesn't stare at a silently-greyed button.
  const capacity = mode === 'refund'
    ? Number(invoice?.paid_total || 0)
    : Number(invoice?.due_total || 0);
  const settlementOK = totals.rounded <= capacity + 0.01;
  const settlementHint = (!settlementOK && totals.rounded > 0) ? (
    mode === 'refund'
      ? `Refund ${fmtINR(totals.rounded)} exceeds the invoice's paid amount ${fmtINR(capacity)}. Reduce the credit qty, or issue two credit notes (one refund up to paid, then an adjustment for the rest).`
      : `Credit ${fmtINR(totals.rounded)} exceeds the invoice's outstanding due ${fmtINR(capacity)}. Reduce the credit qty${canRefund ? `, or switch to Refund mode (up to ${fmtINR(invoice?.paid_total || 0)}).` : '.'}`
  ) : '';
  const canSubmit = !busy && hasQty && reasonOK && refundOK && settlementOK && totals.rounded > 0;

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const body = {
        reason_code: reasonCode,
        reason_text: reasonText.trim(),
        settlement_mode: mode,
        lines: rows.filter((r) => r.qty > 0).map((r) => ({
          line_id: r.line_id, quantity_credited: r.qty,
        })),
      };
      if (mode === 'refund') {
        body.refund_method = refundMethod;
        if (refundRef.trim()) body.refund_reference = refundRef.trim();
      }
      const r = await axios.post(
        `${API}/billing/invoices/${invoice.invoice_id}/credit-notes`,
        body,
      );
      onIssued && onIssued(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to issue credit note.');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 print:hidden"
         data-testid="credit-note-dialog">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div>
            <h3 className="text-sm font-bold text-slate-800">Issue Credit Note</h3>
            <p className="text-[11px] text-slate-500">
              Against <span className="font-mono">{invoice.invoice_no}</span> — {invoice.patient_name}
            </p>
          </div>
          <button onClick={onClose} data-testid="cn-cancel"
            className="text-slate-500 hover:text-slate-800 text-lg leading-none px-2">×</button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-auto px-4 py-3 space-y-4 text-xs">
          {/* Reason */}
          <div className="grid grid-cols-3 gap-3">
            <label className="col-span-1 flex flex-col gap-1">
              <span className="font-semibold text-slate-600 uppercase tracking-wide text-[10px]">Reason</span>
              <select
                data-testid="cn-reason-code"
                value={reasonCode}
                onChange={(e) => setReasonCode(e.target.value)}
                className="border border-slate-300 rounded px-2 py-1 text-xs bg-white font-medium">
                {REASON_CODES.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </label>
            <label className="col-span-2 flex flex-col gap-1">
              <span className="font-semibold text-slate-600 uppercase tracking-wide text-[10px]">
                Details (min {reasonMinLen} chars)
              </span>
              <input
                data-testid="cn-reason-text"
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                placeholder={reasonCode === 'other'
                  ? 'Explain in detail why this credit note is being issued...'
                  : 'e.g. Patient name typed wrong, replaced with corrected invoice.'}
                className={`border rounded px-2 py-1 text-xs bg-white ${
                  reasonText && !reasonOK ? 'border-rose-400' : 'border-slate-300'
                }`} />
            </label>
          </div>

          {/* Line selector */}
          <div>
            <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">
              Lines to credit
            </div>
            <div className="border border-slate-200 rounded overflow-hidden">
              <table className="w-full text-[11px]">
                <thead className="bg-slate-50">
                  <tr className="text-left">
                    <th className="px-2 py-1.5 font-semibold text-slate-600">Description</th>
                    <th className="px-2 py-1.5 font-semibold text-slate-600 text-right">Orig Qty</th>
                    <th className="px-2 py-1.5 font-semibold text-slate-600 text-right">Credit Qty</th>
                    <th className="px-2 py-1.5 font-semibold text-slate-600 text-right">Credit Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.line_id} className="border-t border-slate-100"
                        data-testid={`cn-line-${r.line_id}`}>
                      <td className="px-2 py-1.5 text-slate-700">
                        {r.description}
                        {r.already > 0 && (
                          <span className="ml-2 text-[10px] text-rose-500">
                            already credited: {r.already}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 text-right text-slate-600 tabular-nums">{r.origQty}</td>
                      <td className="px-2 py-1.5 text-right">
                        <input
                          type="number" min="0" step="0.01" max={r.remaining}
                          value={r.qty}
                          onChange={(e) => {
                            // Clamp to the remaining creditable quantity so
                            // the amount preview can't outrun what's actually
                            // creditable.
                            const raw = Number(e.target.value || 0);
                            const clamped = Math.min(Math.max(0, raw), r.remaining);
                            setQty(r.line_id, clamped);
                          }}
                          data-testid={`cn-qty-${r.line_id}`}
                          className="border border-slate-300 rounded px-1 py-0.5 w-20 text-right text-[11px]" />
                        <div className="text-[9px] text-slate-400 mt-0.5">
                          max {r.remaining}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-slate-800">
                        {fmtINR(r.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 border-t border-slate-200">
                  <tr>
                    <td className="px-2 py-1.5 font-semibold text-slate-700" colSpan={3}>
                      Sub-total (taxable) · Tax reversal
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">
                      {fmtINR(totals.subtotal)} · {fmtINR(totals.cgst + totals.sgst + totals.igst)}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-2 py-1.5 font-bold text-slate-800" colSpan={3}>
                      Amount to credit (rounded)
                    </td>
                    <td className="px-2 py-1.5 text-right text-sm font-bold text-rose-600 tabular-nums"
                        data-testid="cn-amount-preview">
                      {fmtINR(totals.rounded)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Settlement mode */}
          <div>
            <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">
              Settlement mode
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className={`flex items-start gap-2 border rounded px-2 py-2 text-[11px] cursor-pointer ${
                mode === 'adjustment' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200'
              } ${!canAdjust ? 'opacity-40 cursor-not-allowed' : ''}`}>
                <input
                  type="radio" name="cn-mode" value="adjustment"
                  data-testid="cn-mode-adjustment"
                  checked={mode === 'adjustment'} disabled={!canAdjust}
                  onChange={() => setMode('adjustment')} />
                <div>
                  <div className="font-semibold">Adjustment (reduce due)</div>
                  <div className="text-slate-500">
                    Reduces outstanding balance without moving cash.
                    {!canAdjust && ' (Not available — no due)'}
                  </div>
                </div>
              </label>
              <label className={`flex items-start gap-2 border rounded px-2 py-2 text-[11px] cursor-pointer ${
                mode === 'refund' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200'
              } ${!canRefund ? 'opacity-40 cursor-not-allowed' : ''}`}>
                <input
                  type="radio" name="cn-mode" value="refund"
                  data-testid="cn-mode-refund"
                  checked={mode === 'refund'} disabled={!canRefund}
                  onChange={() => setMode('refund')} />
                <div>
                  <div className="font-semibold">Refund (return money)</div>
                  <div className="text-slate-500">
                    Records a refund payment on the invoice; you settle offline.
                    {!canRefund && ' (Not available — nothing paid yet)'}
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Refund details — only for refund mode */}
          {mode === 'refund' && (
            <div className="grid grid-cols-3 gap-3">
              <label className="col-span-1 flex flex-col gap-1">
                <span className="font-semibold text-slate-600 uppercase tracking-wide text-[10px]">
                  Refund method
                </span>
                <select
                  data-testid="cn-refund-method"
                  value={refundMethod}
                  onChange={(e) => setRefundMethod(e.target.value)}
                  className="border border-slate-300 rounded px-2 py-1 text-xs bg-white font-medium">
                  {REFUND_METHODS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </label>
              <label className="col-span-2 flex flex-col gap-1">
                <span className="font-semibold text-slate-600 uppercase tracking-wide text-[10px]">
                  Reference (optional — UTR / txn id / last-4)
                </span>
                <input
                  data-testid="cn-refund-reference"
                  value={refundRef}
                  onChange={(e) => setRefundRef(e.target.value)}
                  placeholder="e.g. UPI 987654321 / cheque #0034"
                  className="border border-slate-300 rounded px-2 py-1 text-xs" />
              </label>
            </div>
          )}

          {settlementHint && (
            <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-800"
                 data-testid="cn-settlement-hint">
              <span className="font-semibold">Cannot submit yet: </span>{settlementHint}
            </div>
          )}
          {err && (
            <div className="rounded border border-rose-300 bg-rose-50 px-3 py-2 text-[11px] text-rose-700"
                 data-testid="cn-error">{err}</div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 bg-slate-50">
          <div className="text-[11px] text-slate-500">
            Immutable once issued. Voiding a CN reverses its settlement effect on the invoice.
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} disabled={busy}
              className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded"
              data-testid="cn-dismiss">Cancel</button>
            <button onClick={submit} disabled={!canSubmit}
              data-testid="cn-submit"
              className={`px-4 py-1.5 text-xs font-semibold rounded ${
                canSubmit
                  ? 'bg-rose-600 text-white hover:bg-rose-700'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}>
              {busy ? 'Issuing…' : `Issue Credit Note · ${fmtINR(totals.rounded)}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
